import React, { useState, useEffect } from "react";
import { Button, Tooltip, Badge } from "antd";
import { QuestionCircleOutlined, InfoCircleOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import HelpModal from "./HelpModal";
import { isDocumentViewed, getDocumentViewCount } from "../../utils/helpDocumentTracker";

/**
 * 帮助按钮组件
 * 提供统一的帮助入口，点击后显示对应的帮助文档
 */
const HelpButton = ({ contentPath, title = "帮助文档", tooltip = "查看帮助", type = "text", size = "small", icon = <QuestionCircleOutlined />, style = {}, className = "", modalWidth = 900, showUnviewedIndicator = true, hideAfterViewed = false, ...restProps }) => {
    const [modalVisible, setModalVisible] = useState(false);
    const [isViewed, setIsViewed] = useState(true);
    const [viewCount, setViewCount] = useState(0);

    // 检查文档查看状态
    useEffect(() => {
        if (contentPath) {
            setIsViewed(isDocumentViewed(contentPath));
            setViewCount(getDocumentViewCount(contentPath));
        }
    }, [contentPath]);

    // 监听文档查看事件
    useEffect(() => {
        const handleDocumentViewed = (event) => {
            if (event.detail.contentPath === contentPath) {
                setIsViewed(true);
                setViewCount(event.detail.record.viewCount);
            }
        };

        window.addEventListener("helpDocumentViewed", handleDocumentViewed);
        return () => {
            window.removeEventListener("helpDocumentViewed", handleDocumentViewed);
        };
    }, [contentPath]);

    const handleClick = () => {
        setModalVisible(true);
    };

    const handleClose = () => {
        setModalVisible(false);
    };

    // 根据查看状态确定样式和图标
    const getButtonProps = () => {
        if (!showUnviewedIndicator || isViewed) {
            return {
                icon,
                style: {
                    color: "#1890ff",
                    border: "none",
                    boxShadow: "none",
                    ...style,
                },
            };
        }

        // 未查看的样式 - 更明显的提示
        return {
            icon: <ExclamationCircleOutlined />,
            style: {
                color: "#ff4d4f",
                border: "1px solid #ff4d4f",
                backgroundColor: "#fff2f0",
                boxShadow: "0 2px 4px rgba(255, 77, 79, 0.2)",
                animation: "pulse 2s infinite",
                ...style,
            },
        };
    };

    // 如果启用了看过即隐藏，且用户已看过，并且模态框未显示，则不渲染任何内容
    if (hideAfterViewed && isViewed && contentPath && !modalVisible) {
        return null;
    }

    const buttonProps = getButtonProps();
    const finalTooltip = !isViewed && showUnviewedIndicator ? `🔥 新功能说明 - ${tooltip}` : viewCount > 0 ? `${tooltip} (${viewCount})` : tooltip;

    const buttonElement = <Button type={type} size={size} onClick={handleClick} className={`help-button ${className} ${!isViewed && showUnviewedIndicator ? "help-button-unviewed" : ""}`} {...buttonProps} {...restProps} />;

    return (
        <>
            <Tooltip title={finalTooltip}>
                {!isViewed && showUnviewedIndicator ? (
                    <Badge dot color="#ff4d4f" offset={[-2, 2]}>
                        {buttonElement}
                    </Badge>
                ) : (
                    buttonElement
                )}
            </Tooltip>

            <HelpModal visible={modalVisible} onClose={handleClose} contentPath={contentPath} title={title} width={modalWidth} />
        </>
    );
};

/**
 * 内联帮助按钮 - 用于在标题或文本旁边显示
 */
export const InlineHelpButton = ({ contentPath, title, tooltip, showUnviewedIndicator = true, hideAfterViewed = false, ...props }) => {
    const [isViewed, setIsViewed] = useState(true);

    useEffect(() => {
        if (contentPath) {
            setIsViewed(isDocumentViewed(contentPath));
        }
    }, [contentPath]);

    useEffect(() => {
        const handleDocumentViewed = (event) => {
            if (event.detail.contentPath === contentPath) {
                setIsViewed(true);
            }
        };

        window.addEventListener("helpDocumentViewed", handleDocumentViewed);
        return () => {
            window.removeEventListener("helpDocumentViewed", handleDocumentViewed);
        };
    }, [contentPath]);

    return (
        <HelpButton
            contentPath={contentPath}
            title={title}
            tooltip={tooltip}
            type="text"
            size="small"
            icon={<InfoCircleOutlined />}
            showUnviewedIndicator={showUnviewedIndicator}
            hideAfterViewed={hideAfterViewed}
            style={{
                marginLeft: 4,
                fontSize: 12,
                color: !isViewed && showUnviewedIndicator ? "#ff4d4f" : "#8c8c8c",
                padding: 0,
                height: "auto",
                lineHeight: 1,
                fontWeight: !isViewed && showUnviewedIndicator ? "bold" : "normal",
            }}
            {...props}
        />
    );
};

/**
 * 工具栏帮助按钮 - 用于在工具栏中显示
 */
export const ToolbarHelpButton = ({ contentPath, title, tooltip, showUnviewedIndicator = true, hideAfterViewed = false, ...props }) => {
    const [isViewed, setIsViewed] = useState(true);

    useEffect(() => {
        if (contentPath) {
            setIsViewed(isDocumentViewed(contentPath));
        }
    }, [contentPath]);

    useEffect(() => {
        const handleDocumentViewed = (event) => {
            if (event.detail.contentPath === contentPath) {
                setIsViewed(true);
            }
        };

        window.addEventListener("helpDocumentViewed", handleDocumentViewed);
        return () => {
            window.removeEventListener("helpDocumentViewed", handleDocumentViewed);
        };
    }, [contentPath]);

    return (
        <HelpButton
            contentPath={contentPath}
            title={title}
            tooltip={tooltip}
            type="default"
            size="small"
            icon={<QuestionCircleOutlined />}
            showUnviewedIndicator={showUnviewedIndicator}
            hideAfterViewed={hideAfterViewed}
            style={{
                borderColor: !isViewed && showUnviewedIndicator ? "#ff4d4f" : "#d9d9d9",
                color: !isViewed && showUnviewedIndicator ? "#ff4d4f" : "#595959",
                backgroundColor: !isViewed && showUnviewedIndicator ? "#fff2f0" : "transparent",
            }}
            {...props}
        />
    );
};

export default HelpButton;
